export type WidgetBrandingConfig = {
  clientId?: string;
  apiBaseUrl?: string;
  position?: 'bottom-right' | 'bottom-left';
  positionMode?: 'fixed' | 'absolute';
  primaryColor?: string;
  botName?: string;
  taglineText?: string;
  welcomeMessage?: string;
  launcherColor?: string;
  launcherIcon?: string;
  launcherText?: string;
  launcherTeaser?: string;
  launcherSize?: number;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  panelBackgroundColor?: string;
  cardRadius?: number;
  panelWidth?: number;
  panelHeight?: number;
  aiBubbleBg?: string;
  aiTextColor?: string;
  userBubbleBg?: string;
  userTextColor?: string;
  bubbleRadius?: number;
  bubblePadding?: number;
  bubbleFontSize?: number;
  inputBackgroundColor?: string;
  inputTextColor?: string;
  inputBorderColor?: string;
  inputPlaceholder?: string;
  inputPlaceholderColor?: string;
  sendButtonBgColor?: string;
  sendButtonTextColor?: string;
  fontFamily?: string;
  showBubbles?: boolean;
  showThumbsUp?: boolean;
  showThumbsDown?: boolean;
  feedbackHint?: string;
  preview?: boolean;
  container?: HTMLElement | null;
  brandingOverride?: Record<string, any> | null;
};

const asNumber = (value: any): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const buildWidgetConfig = (
  base: WidgetBrandingConfig,
  branding: Record<string, any> | null | undefined
): WidgetBrandingConfig => {
  if (!branding) return { ...base };

  const primaryColor = branding.primaryColor || base.primaryColor || '#4F46E5';
  const launcherColor = branding.launcherColor || primaryColor;
  const launcherSize = asNumber(branding.launcherSize) ?? base.launcherSize ?? 56;
  const showBubbles = branding.showBubbles !== false;

  return {
    ...base,
    position: branding.launcherPosition || base.position,
    positionMode: base.positionMode || 'fixed',
    primaryColor,
    botName: branding.companyName || branding.appName || base.botName,
    taglineText: branding.taglineText || branding.tagline || base.taglineText,
    welcomeMessage: branding.emptyStateText || base.welcomeMessage,
    launcherColor,
    launcherIcon: branding.launcherIcon || base.launcherIcon || '💬',
    launcherText: branding.launcherText || base.launcherText,
    launcherTeaser: branding.launcherTeaser || base.launcherTeaser,
    launcherSize,
    headerBackgroundColor: branding.headerBackgroundColor || primaryColor,
    headerTextColor: branding.headerTextColor || '#ffffff',
    panelBackgroundColor: branding.chatBackgroundColor || branding.cardBackgroundColor || base.panelBackgroundColor,
    cardRadius: asNumber(branding.cardRadius) ?? asNumber(branding.cardBorderRadius) ?? base.cardRadius,
    panelWidth: asNumber(branding.widgetMaxWidth) ?? base.panelWidth,
    panelHeight: asNumber(branding.widgetMaxHeight) ?? base.panelHeight,
    aiBubbleBg: showBubbles ? (branding.aiBubbleBg || base.aiBubbleBg) : 'transparent',
    aiTextColor: branding.aiTextColor || base.aiTextColor,
    userBubbleBg: showBubbles ? (branding.userBubbleBg || primaryColor || base.userBubbleBg) : 'transparent',
    userTextColor: branding.userTextColor || base.userTextColor,
    bubbleRadius: asNumber(branding.bubbleRadiusPx) ?? asNumber(branding.bubbleRadius) ?? base.bubbleRadius,
    bubblePadding: asNumber(branding.bubblePadding) ?? base.bubblePadding,
    bubbleFontSize: asNumber(branding.bubbleFontSize) ?? base.bubbleFontSize,
    inputBackgroundColor: branding.inputBackgroundColor || base.inputBackgroundColor,
    inputTextColor: branding.inputTextColor || base.inputTextColor,
    inputBorderColor: branding.inputBorderColor || base.inputBorderColor,
    inputPlaceholder: branding.inputPlaceholder || base.inputPlaceholder,
    inputPlaceholderColor: branding.inputPlaceholderColor || base.inputPlaceholderColor,
    sendButtonBgColor: branding.sendButtonBgColor || primaryColor || base.sendButtonBgColor,
    sendButtonTextColor: branding.sendButtonTextColor || base.sendButtonTextColor,
    fontFamily: branding.fontFamily || base.fontFamily,
    showBubbles,
    showThumbsUp: branding.showThumbsUp ?? base.showThumbsUp,
    showThumbsDown: branding.showThumbsDown ?? base.showThumbsDown,
    feedbackHint: branding.feedbackHint || base.feedbackHint,
  };
};

export const buildWidgetStyles = (config: WidgetBrandingConfig) => `
  .luxon-widget-button {
    position: ${config.positionMode || 'fixed'};
    ${config.position === 'bottom-left' ? 'left: 20px;' : 'right: 20px;'}
    bottom: 20px;
    width: ${config.launcherSize}px;
    height: ${config.launcherSize}px;
    border-radius: 50%;
    background: ${config.launcherColor};
    color: white;
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    z-index: 999999;
    transition: transform 0.2s, box-shadow 0.2s;
    pointer-events: auto;
  }
  .luxon-widget-button:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0,0,0,0.2);
  }
  .luxon-widget-teaser {
    position: ${config.positionMode || 'fixed'};
    ${config.position === 'bottom-left' ? 'left: 20px;' : 'right: 20px;'}
    bottom: ${config.launcherSize ? config.launcherSize + 28 : 84}px;
    background: white;
    color: #111827;
    font-size: 12px;
    padding: 6px 10px;
    border-radius: 999px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
    z-index: 999999;
    max-width: 220px;
  }
  .luxon-widget-panel {
    position: ${config.positionMode || 'fixed'};
    ${config.position === 'bottom-left' ? 'left: 20px;' : 'right: 20px;'}
    bottom: ${config.launcherSize ? config.launcherSize + 30 : 90}px;
    width: ${config.panelWidth}px;
    height: ${config.panelHeight}px;
    max-height: calc(100vh - 120px);
    background: ${config.panelBackgroundColor};
    border-radius: ${config.cardRadius}px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
    display: none;
    flex-direction: column;
    z-index: 999999;
    font-family: ${config.fontFamily};
    overflow: hidden;
    will-change: transform;
    contain: layout style;
    pointer-events: auto;
  }
  .luxon-widget-panel.open {
    display: flex;
  }
  .luxon-widget-header {
    background: ${config.headerBackgroundColor};
    color: ${config.headerTextColor};
    padding: 16px;
    border-radius: ${config.cardRadius}px ${config.cardRadius}px 0 0;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  .luxon-widget-header-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .luxon-widget-header-subtitle {
    font-size: 12px;
    font-weight: 500;
    opacity: 0.9;
  }
  .luxon-widget-close {
    background: none;
    border: none;
    color: ${config.headerTextColor};
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  }
  .luxon-widget-close:hover {
    background: rgba(255,255,255,0.1);
  }
  .luxon-widget-messages {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overscroll-behavior: contain;
    scroll-behavior: auto;
  }
  .luxon-widget-message {
    max-width: 80%;
    padding: ${config.bubblePadding}px;
    border-radius: ${config.bubbleRadius}px;
    word-wrap: break-word;
    font-size: ${config.bubbleFontSize}px;
  }
  .luxon-widget-message.user {
    align-self: flex-end;
    background: ${config.userBubbleBg};
    color: ${config.userTextColor};
  }
  .luxon-widget-message.assistant {
    align-self: flex-start;
    background: ${config.aiBubbleBg};
    color: ${config.aiTextColor};
  }
  .luxon-widget-feedback {
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: ${config.aiTextColor};
    opacity: 0.8;
  }
  .luxon-widget-feedback button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
  }
  .luxon-widget-input-container {
    padding: 16px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 8px;
  }
  .luxon-widget-input {
    flex: 1;
    padding: 10px 14px;
    border: 1px solid ${config.inputBorderColor};
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    font-family: inherit;
    background: ${config.inputBackgroundColor};
    color: ${config.inputTextColor};
  }
  .luxon-widget-input::placeholder {
    color: ${config.inputPlaceholderColor};
  }
  .luxon-widget-input:focus {
    border-color: ${config.primaryColor};
  }
  .luxon-widget-send {
    padding: 10px 20px;
    background: ${config.sendButtonBgColor};
    color: ${config.sendButtonTextColor};
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: opacity 0.2s;
  }
  .luxon-widget-send:hover {
    opacity: 0.9;
  }
  .luxon-widget-send:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
